/**
 * RS Ship Complete Toggle Component
 * Functions for toggling ship-complete-button states
 *
 * Usage Examples:
 *
 * // Initialize all ship-complete buttons on the page (automatic on DOM ready)
 * RsShipComplete.init();
 *
 * // Initialize specific ship-complete button
 * const button = document.querySelector('.ship-complete-button');
 * RsShipComplete.init(button);
 *
 * // Programmatically toggle button state
 * RsShipComplete.toggle(button);
 * RsShipComplete.setActive(button, true);  // Set to active
 * RsShipComplete.setActive(button, false); // Set to inactive
 * RsShipComplete.isActive(button);         // Check if active
 */

// Selectors for ship-complete component
const SHIP_COMPLETE_SELECTORS = {
  button: '.ship-complete-button',
  activeClass: 'is-active',
  iconOff: '.icon--off',
  iconOn: '.icon--on',
  banner: '.cart-page-actions__ship-complete-banner',
  bannerClose: '.cart-page-actions__ship-complete-banner-close',
};

// Helper functions
/** @type {(selector: string, root?: Document | HTMLElement) => HTMLElement | null} */
const $ship = (selector, root = document) => root.querySelector(selector);
/** @type {(selector: string, root?: Document | HTMLElement) => Element[]} */
const $$ship = (selector, root = document) => [...root.querySelectorAll(selector)];

/** Format Date -> MM/DD/YYYY */
function formatMDY(date) {
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  const yyyy = date.getFullYear();
  return `${mm}/${dd}/${yyyy}`;
}

/**
 * Parse one line item’s availability banners:
 *  - sum quantities
 *  - find latest date
 * @returns {{ totalQty:number, latestDate: Date | null }}
 */
function parseLineItemAvailability(lineItem) {
  const banners = lineItem
    ? lineItem.querySelectorAll(
      '.cart-table__availability-banner, .rs-cart-line-availability .rs-table__item'
    )
    : [];
  
  let parsedQty = 0;
  let latest = null;
  
  banners.forEach((b) => {
    const txt = b.textContent || '';
    const mQty = txt.match(/(\d+)\s+units/i);

    if (mQty && mQty[1]) parsedQty += parseInt(mQty[1], 10) || 0;

    extractShipDatesFromText(txt).forEach((d) => {
      if (!latest || d > latest) latest = d;
    });
  });
  
  return { totalQty: calculateTotalQuantity(lineItem) || parsedQty, latestDate: latest };
}

/**
 * Calculate total quantity from all line items
 * @returns {number} Total quantity across all line items
 * @param {Element | null | undefined} lineItem
 */
function calculateTotalQuantity(lineItem) {
  const quantityInputs = lineItem ? lineItem.querySelector('.rs-quantity-input') : document.querySelectorAll('.rs-quantity-input');
  let totalQuantity = 0;

  if (lineItem && quantityInputs instanceof HTMLInputElement) {
    const quantity = parseInt(quantityInputs.value) || 0;
    totalQuantity += quantity;
  } else if (quantityInputs instanceof NodeList) {
    quantityInputs.forEach((/** @type {Element} */ input) => {
      if (input instanceof HTMLInputElement) {
        const quantity = parseInt(input.value) || 0;
        totalQuantity += quantity;
      }
    });
  }

  return totalQuantity;
}

const LOCALE_MONTH_DATE_RE =
  /\b(?:January|February|March|April|May|June|July|August|September|October|November|December)\s+\d{1,2},?\s+\d{4}\b/i;
const LOCALE_MONTH_DATE_GLOBAL_RE = new RegExp(LOCALE_MONTH_DATE_RE.source, 'gi');

/**
 * @param {Date} date
 * @returns {Date}
 */
function normalizeShipDateForCompare(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 12, 0, 0, 0);
}

/**
 * @param {string} str
 * @returns {Date | null}
 */
function parseAvailabilityShipDate(str) {
  const t = String(str || '').trim();
  if (!t) return null;

  const iso = t.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) {
    return normalizeShipDateForCompare(new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3])));
  }

  const mdY = t.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (mdY) {
    return normalizeShipDateForCompare(new Date(Number(mdY[3]), Number(mdY[1]) - 1, Number(mdY[2])));
  }

  const locale = LOCALE_MONTH_DATE_RE.exec(t);
  if (locale?.[0]) {
    const parsed = Date.parse(locale[0]);
    if (!Number.isNaN(parsed)) {
      return normalizeShipDateForCompare(new Date(parsed));
    }
  }

  const parsed = Date.parse(t);
  if (!Number.isNaN(parsed)) {
    return normalizeShipDateForCompare(new Date(parsed));
  }

  return null;
}

/**
 * @param {string} text
 * @returns {Date[]}
 */
function extractShipDatesFromText(text) {
  const t = String(text || '').trim();
  if (!t) return [];

  /** @type {Date[]} */
  const dates = [];
  const seen = new Set();

  const add = (/** @type {Date | null} */ d) => {
    if (!d) return;
    const key = d.getTime();
    if (seen.has(key)) return;
    seen.add(key);
    dates.push(d);
  };

  const mdY = /(\d{1,2}\/\d{1,2}\/\d{4})/g;
  let match;
  while ((match = mdY.exec(t)) !== null) {
    add(parseAvailabilityShipDate(match[1]));
  }

  LOCALE_MONTH_DATE_GLOBAL_RE.lastIndex = 0;
  while ((match = LOCALE_MONTH_DATE_GLOBAL_RE.exec(t)) !== null) {
    add(parseAvailabilityShipDate(match[0]));
  }

  if (!dates.length) {
    add(parseAvailabilityShipDate(t));
  }

  return dates;
}

/**
 * @param {Element} banner
 * @returns {Date[]}
 */
function extractShipDatesFromBanner(banner) {
  const strong = banner.querySelector('strong.gray-600');
  const sources = [strong?.textContent?.trim(), banner.textContent?.trim()].filter(Boolean);
  /** @type {Date[]} */
  const dates = [];
  const seen = new Set();

  sources.forEach((raw) => {
    extractShipDatesFromText(/** @type {string} */ (raw)).forEach((d) => {
      const key = d.getTime();
      if (seen.has(key)) return;
      seen.add(key);
      dates.push(d);
    });
  });

  return dates;
}

/**
 * @param {Date | null} current
 * @param {Date | null} candidate
 * @returns {Date | null}
 */
function laterShipDate(current, candidate) {
  if (!candidate) return current;
  if (!current) return candidate;
  return candidate > current ? candidate : current;
}

/**
 * Collect ship/availability dates from a cart row (banners, req. delivery inputs, clone attrs).
 * @param {Element} row
 * @returns {Date[]}
 */
function collectShipDatesFromCartRow(row) {
  /** @type {Date[]} */
  const dates = [];

  row.querySelectorAll('.cart-table__availability-banner').forEach((banner) => {
    extractShipDatesFromBanner(banner).forEach((d) => dates.push(d));
  });

  row.querySelectorAll(
    '.cart-table__requested-delivery-date input, .rs-table-body__cell--req-delivery input'
  ).forEach((input) => {
    if (!(input instanceof HTMLInputElement)) return;
    const val = (input.value || input.getAttribute('value') || '').trim();
    if (!val || val.toLowerCase().includes('mm/dd')) return;
    const d = parseAvailabilityShipDate(val);
    if (d) dates.push(d);
  });

  const dataDate = row.getAttribute('data-delivery-date');
  if (dataDate) {
    const d = parseAvailabilityShipDate(dataDate);
    if (d) dates.push(d);
  }

  return dates;
}

/**
 * Furthest ship date from the last cart ATP material list (when present).
 * @returns {Date | null}
 */
function findFurthestShipDateFromRtpMaterialList() {
  const list = /** @type {any} */ (window).RTP?.lastCartMaterialList;
  if (!Array.isArray(list)) return null;

  /** @type {Date | null} */
  let furthest = null;

  list.forEach((/** @type {any} */ item) => {
    const availability = Array.isArray(item?.availability) ? item.availability : [];
    availability.forEach((/** @type {any} */ block) => {
      if (parseInt(block?.availableQty, 10) <= 0) return;
      const d = parseAvailabilityShipDate(String(block?.availableDate ?? ''));
      furthest = laterShipDate(furthest, d);
    });
  });

  return furthest;
}

/**
 * Furthest (latest) ship date for one cart row.
 * @param {Element} row
 * @returns {Date | null}
 */
function findFurthestShipDateForRow(row) {
  /** @type {Date | null} */
  let furthest = null;

  collectShipDatesFromCartRow(row).forEach((d) => {
    furthest = laterShipDate(furthest, d);
  });

  return furthest;
}

/**
 * Furthest ship date across the cart (latest date among all line availability / delivery dates).
 * @returns {Date | null}
 * @param {Element | null | undefined} lineItem
 */
function findLatestShipDate(lineItem) {
  if (lineItem) {
    const row = lineItem.closest?.('.rs-table-body__row') || lineItem;
    return findFurthestShipDateForRow(row);
  }

  /** @type {Date | null} */
  let furthest = findFurthestShipDateFromRtpMaterialList();

  document.querySelectorAll('.cart-table__body .rs-table-body__row, .cart-table .rs-table-body__row').forEach(
    (row) => {
      furthest = laterShipDate(furthest, findFurthestShipDateForRow(row));
    }
  );

  if (furthest) return furthest;

  document.querySelectorAll('.cart-table__availability-banner').forEach((banner) => {
    extractShipDatesFromBanner(banner).forEach((d) => {
      furthest = laterShipDate(furthest, d);
    });
  });

  return furthest;
}

/**
 * Create a combined availability banner with total quantity and latest ship date
 * @param {Element | null | undefined} lineItem
 */
function createCombinedAvailabilityBanner(lineItem) {
  // Remove existing combined banner if it exists
  removeCombinedAvailabilityBanner(lineItem);
  const { totalQty, latestDate } = parseLineItemAvailability(lineItem);
  if (!totalQty || !latestDate) return;
  const list = lineItem?.querySelector('.rs-cart-line-availability .rs-table__list');
  if (!(list instanceof HTMLUListElement)) return;
  
  lineItem?.querySelectorAll(
    '.cart-table__availability-banner, .rs-cart-line-availability .rs-table__item'
  ).forEach((b) => {
    if (b instanceof HTMLElement) b.style.display = 'none';
  });
  
  const combinedBanner = `
    <li class="rs-table__item cart-table__availability-banner--combined rs-pd-x-12 rs-pd-y-4 text-small rs-flex rs-align-center rs-gap-16">
      <span>${totalQty} units will ship on <strong class="gray-600">${formatMDY(latestDate)}</strong></span>
    </li>
  `;
  list.insertAdjacentHTML('afterbegin', combinedBanner);
}

/**
 * Remove the combined availability banner
 * @param {Element | null | undefined} [lineItem]
 */
function removeCombinedAvailabilityBanner(lineItem) {
  lineItem?.querySelectorAll('.cart-table__availability-banner--combined').forEach((banner) => {
    banner.remove();
  });
  lineItem?.querySelectorAll(
    '.cart-table__availability-banner, .rs-cart-line-availability .rs-table__item'
  ).forEach((b) => {
    if (b instanceof HTMLElement) b.style.removeProperty('display');
  });
}

/**
 * Update the combined availability banner with current data
 * @param {Element | null | undefined} lineItem
 *
 * This says is is not used but it is used in the code
 */
function updateCombinedAvailabilityBanner(lineItem) {
  // Check if any ship complete buttons are active
  const activeButtons = document.querySelectorAll(`${SHIP_COMPLETE_SELECTORS.button}.${SHIP_COMPLETE_SELECTORS.activeClass}`);

  if (activeButtons.length > 0) {
    createCombinedAvailabilityBanner(lineItem);
  } else {
    removeCombinedAvailabilityBanner(lineItem);
  }
}

/**
 * Toggle the active state of a ship-complete button
 * @param {HTMLElement} button - The ship-complete button element
 */
function toggleShipComplete(button) {
  if (!button) return;

  const isActive = button.classList.contains(SHIP_COMPLETE_SELECTORS.activeClass);
  const nextActive = !isActive;
  setShipCompleteActive(button, nextActive);
  checkLineItems(button, nextActive);

  /** @type {any} */ (window).cartMutationsInstance?.persistShipCompleteToggle?.(button, nextActive);
}

/**
 * @param {boolean} active
 */
function showShipCompleteBanner(active) {
  const banner = $ship(SHIP_COMPLETE_SELECTORS.banner);
  if (active) {
    if (banner) {
      banner.classList.add('is-active');
      updateShipCompleteBanner();
    }
  } else {
    if (banner) {
      banner.classList.remove('is-active');
    }
  }
}

function updateShipCompleteBanner() {
  const banner = $ship(SHIP_COMPLETE_SELECTORS.banner);
  if (!banner) return;

  const bannerDate = banner.querySelector('.ship-complete-banner__date');
  const dateDetail = banner.querySelector('.ship-complete-banner__date-detail');
  const furthestShipDate = findLatestShipDate();

  if (furthestShipDate && bannerDate) {
    bannerDate.textContent = formatMDY(furthestShipDate);
    if (dateDetail instanceof HTMLElement) {
      dateDetail.hidden = false;
    }
    // Persist to sessionStorage so the order review page can use the same date
    try { sessionStorage.setItem('rs_ship_complete_date', furthestShipDate.toISOString()); } catch {}
  } else if (dateDetail instanceof HTMLElement) {
    dateDetail.hidden = true;
    if (bannerDate) {
      bannerDate.textContent = '';
    }
  }
}


/**
 * @param {any} cart
 * @returns {boolean}
 */
function getShipCompleteFromCartAttributes(cart) {
  const raw = cart?.attributes?._ship_complete ?? cart?.attributes?.ship_complete;
  if (raw === undefined || raw === null || String(raw).trim() === '') {
    return false;
  }
  return String(raw).toLowerCase() === 'true';
}

/**
 * Restore ship-complete UI from cart attributes (survives page refresh).
 * @param {any} [cart]
 */
async function syncWithCartStateFromCartApi(cart) {
  try {
    const cartData =
      cart ||
      (await fetch('/cart.js', {credentials: 'same-origin'}).then((res) => res.json()));

    const desiredActive = getShipCompleteFromCartAttributes(cartData);

    const globalBtn = document.querySelector(
      `.cart-page-actions__ship-complete ${SHIP_COMPLETE_SELECTORS.button}`
    );

    if (globalBtn) setShipCompleteActive(/** @type {HTMLElement} */ (globalBtn), desiredActive);

    document.querySelectorAll('.cart-table__body .rs-table-body__row, .cart-table .rs-table-body__row').forEach(
      (row) => {
        if (desiredActive) {
          updateLineItem(row, true);
          const lineBtn = row.querySelector('.cart-table__ship-complete .ship-complete-button');
          if (lineBtn) setShipCompleteActive(/** @type {HTMLElement} */ (lineBtn), true);
        } else {
          if (row.getAttribute('data-multiple-delivery') === 'true') return;
          // `data-line-item-id` is the variant id, so rows sharing a variant would read
          // each other's `_ship_complete`; CartMutations resolves the row's own line.
          const item = /** @type {any} */ (window).cartMutationsInstance?.resolveCartLineForRow?.(
            cartData,
            row
          );
          const lineActive = item?.properties?._ship_complete === 'true';
          const lineBtn = row.querySelector('.cart-table__ship-complete .ship-complete-button');
          if (lineBtn) setShipCompleteActive(/** @type {HTMLElement} */ (lineBtn), lineActive);
          updateLineItem(row, lineActive);
        }
      }
    );

    showShipCompleteBanner(desiredActive);
  } catch (e) {
    console.warn('Ship Complete: failed to sync from /cart.js', e);
  }
}

/**
 * @param {HTMLElement} button
 * @param {boolean} active
 */
function checkLineItems(button, active) {
  if (!button) return;
  
  const isGlobal = !!button.closest('.cart-page-actions__ship-complete');
  const lineItemButtons = document.querySelectorAll(SHIP_COMPLETE_SELECTORS.button);
  
  if (isGlobal) {
    // Global toggle clicked → update ALL line items + their buttons
    lineItemButtons.forEach((lineItemButton) => {
      const row = lineItemButton.closest('.rs-table-body__row');
      updateLineItem(row, active);
      setShipCompleteActive(lineItemButton, active); // ensure aria/icons sync
    });

    showShipCompleteBanner(active);
  } else {
    const row = button.closest('.rs-table-body__row');
    updateLineItem(row, active);
  }
  
  // Reflect aggregate state on the GLOBAL button
  const activeLineButtons = document.querySelectorAll(
    `${SHIP_COMPLETE_SELECTORS.button}.${SHIP_COMPLETE_SELECTORS.activeClass}`
  );
  const globalBtn = document.querySelector(
    `.cart-page-actions__ship-complete ${SHIP_COMPLETE_SELECTORS.button}`
  );
  
  if (globalBtn) {
    const allActive = activeLineButtons.length > 0 &&
      activeLineButtons.length === lineItemButtons.length;
    setShipCompleteActive(globalBtn, allActive);
    showShipCompleteBanner(allActive);
  }
}

/**
 * @param {Element | null} lineItem
 * @param {boolean} active
 */
function updateLineItem(lineItem, active) {
  if (!lineItem) return;
  
  const lineItemShipCompleteToggle =
    lineItem.querySelector('.ship-complete-toggle__button');
  
  if (active) {
    // Hide individual availability banners
    lineItem.querySelectorAll('.cart-table__availability-banner').forEach((b) => {
      if (b instanceof HTMLElement) b.style.display = 'none';
    });
    // Flip the line toggle ON using the canonical setter
    if (lineItemShipCompleteToggle) {
      setShipCompleteActive(lineItemShipCompleteToggle, true);
    }

    createCombinedAvailabilityBanner(lineItem);
  } else {
    // Show individual banners
    lineItem.querySelectorAll('.cart-table__availability-banner').forEach((b) => {
      if (b instanceof HTMLElement) b.style.display = 'block';
    });
    
    // Flip the line toggle OFF using the canonical setter
    if (lineItemShipCompleteToggle) {
      setShipCompleteActive(lineItemShipCompleteToggle, false);
    }
    
    removeCombinedAvailabilityBanner(lineItem);
  }
}

/**
 * Set the active state of a ship-complete button
 * @param {HTMLElement} button - The ship-complete button element
 * @param {boolean} active - Whether the button should be active
 */
function setShipCompleteActive(button, active) {
  if (!button) return;

  button.classList.toggle(SHIP_COMPLETE_SELECTORS.activeClass, active);

  // Update aria-pressed for accessibility
  button.setAttribute('aria-pressed', active.toString());

  // Update icon visibility
  iconVisibility(button, active);
  // Disable/Enable multiple delivery dates modal trigger
  toggleMultipleDeliveryDatesModalTrigger(active);
}

/**
 * @param {boolean} active
 */
function toggleMultipleDeliveryDatesModalTrigger(active) {
  const multipleDeliveryDatesModalTrigger = document.querySelectorAll('.multiple-delivery-dates-trigger');
  multipleDeliveryDatesModalTrigger.forEach((trigger) => {
    if (trigger instanceof HTMLElement) {
      trigger.disabled = active ? 'disabled' : '';
    }
  });
}

/**
 * @param {HTMLElement} button
 * @param {boolean} active
 *
 * This says is is not used but it is used in the code
 */
function iconVisibility(button, active) {
  const iconOff = $ship(SHIP_COMPLETE_SELECTORS.iconOff, button);
  const iconOn = $ship(SHIP_COMPLETE_SELECTORS.iconOn, button);
  if (iconOff && iconOn) {
    iconOff.style.display = active ? 'none' : 'block';
    iconOn.style.display = active ? 'block' : 'none';
  }
}

/**
 * Check if a ship-complete button is active
 * @param {HTMLElement} button - The ship-complete button element
 * @returns {boolean} Whether the button is active
 */
function isShipCompleteActive(button) {
  if (!button) return false;
  return button.classList.contains(SHIP_COMPLETE_SELECTORS.activeClass);
}

/**
 * Initialize ship-complete functionality for a specific button or all buttons
 * @param {HTMLElement|string} [target] - Specific button element or selector, defaults to all buttons
 */
function initShipComplete(target) {
  const buttons = target ? (typeof target === 'string' ? $$ship(target) : [target]) : $$ship(SHIP_COMPLETE_SELECTORS.button);

  buttons.forEach((button) => {
    if (!button) return;

    // Set initial state attributes for accessibility
    button.setAttribute('role', 'button');
    button.setAttribute('aria-pressed', 'false');
    button.setAttribute('aria-label', 'Toggle ship complete');

    // Add click event listener
    button.addEventListener('click', (/** @type {Event} */ e) => {
      e.preventDefault();
      toggleShipComplete(/** @type {HTMLElement} */ (button));
    });

    // Add keyboard support (Enter and Space)
    button.addEventListener('keydown', (/** @type {KeyboardEvent} */ e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        toggleShipComplete(/** @type {HTMLElement} */ (button));
      }
    });
  });

  // Check cart attributes for ship complete
  checkCartAttributes();
}

/**
 * Check cart attributes and sync ship complete state
 */
async function checkCartAttributes() {
  if (/** @type {any} */ (window).cartMutationsInstance?.syncWithCartState) {
    /** @type {any} */ (window).cartMutationsInstance.syncWithCartState();
  } else {
    // Local sync from API
    await syncWithCartStateFromCartApi();
  }
}

/**
 * Set up global event listeners for ship-complete buttons
 * This should be called once when the page loads
 */
function setupShipCompleteGlobalListeners() {
  // Handle dynamic content (if buttons are added after page load)
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) {
          const newButtons = $$ship(SHIP_COMPLETE_SELECTORS.button, /** @type {HTMLElement} */ (node));
          if (newButtons.length > 0) {
            initShipComplete(/** @type {HTMLElement} */ (node));
          }
        }
      });
    });
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
  });
}

/**
 * Close the ship complete banner
 */
function closeShipCompleteBanner() {
  const banner = $ship(SHIP_COMPLETE_SELECTORS.banner);
  const bannerClose = $ship(SHIP_COMPLETE_SELECTORS.bannerClose);
  if (bannerClose) {
    bannerClose.addEventListener('click', () => {
      if (banner) banner.classList.remove('is-active');
    });
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  initShipComplete();
  setupShipCompleteGlobalListeners();
  closeShipCompleteBanner();
});

document.addEventListener('cart:rows-updated', async () => {
  await syncWithCartStateFromCartApi();
});

window.rsShipComplete = {
  toggleShipComplete: toggleShipComplete,
  updateShipCompleteBanner: updateShipCompleteBanner,
  showShipCompleteBanner: showShipCompleteBanner,
  findLatestShipDate: findLatestShipDate,
  createCombinedAvailabilityBanner: createCombinedAvailabilityBanner,
  syncFromCart: syncWithCartStateFromCartApi,
  getShipCompleteFromCartAttributes: getShipCompleteFromCartAttributes,
};

// Export functions for use in other modules
if (typeof window !== 'undefined') {
  /** @type {any} */
  (window).RsShipComplete = {
    toggle: toggleShipComplete,
    setActive: setShipCompleteActive,
    isActive: isShipCompleteActive,
    init: initShipComplete,
    setupGlobalListeners: setupShipCompleteGlobalListeners,
    showShipCompleteBanner: showShipCompleteBanner,
    findLatestShipDate: findLatestShipDate,
    createCombinedAvailabilityBanner: createCombinedAvailabilityBanner,
    syncFromCart: syncWithCartStateFromCartApi,
  };
}
